import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useServices } from "../context/ServiceContext";
import { useToastContext } from "../context/ToastContext";
import { usePlans } from "../hooks/usePlans";
import type { serviceStruct } from "../interfaces/serviceInterface";
import MainLayout from "../layouts/MainLayout";
import { Box, Typography, Button, Card, Chip, Tabs, Tab } from "@mui/material";
import { ArrowLeft, Wifi, Smartphone, MapPin, CheckCircle2, AlertCircle, Zap, Activity, Info, Loader, FileText, AlertTriangle, ShoppingCart } from "lucide-react";
import RechargeWizard from "../components/RechargeWizard";
import WifiSettings from "../components/WifiSettings";
import { calculateRemainingDays } from "@/tools/reamingDays";
import { formatCurrency } from "../tools/formatCurrency";

export default function ServiceDetails() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { services, loading: servicesLoading } = useServices();
  const { error: showError } = useToastContext();

  const [service, setService] = useState<serviceStruct | null>(null);
  const [loading, setLoading] = useState(true);
  const [instalationProcess, setInstalationProcess] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"plan" | "info" | "recharge" | "diagnostics" | "wifi">("info");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [instalationLoading, setInstalationLoading] = useState(false);
  const { plans, loading: plansLoading } = usePlans(serviceId);

  // Set default tab to recharge for prepaid services
  useEffect(() => {
    if (service) {
      const planData = service.plan[0];
      const isPostpaid = planData?.serviceType?.toLowerCase().includes("pospago");
      if (!isPostpaid) {
        setActiveTab("recharge");
      }

      if (service.installationInfo.status === "INSTALADO" || service.installationInfo.status === "INSTALACION EN CURSO" || service.installationInfo.status === "PENDIENTE DE INSTALACION") {
        setInstalationProcess(true);
      } else {
        setInstalationProcess(false);
      }

    }
  }, [service]);

  // Filtrar planes raíz (sin chillOfId)
  const rootPlans = plans.filter((plan: any) => !plan.chillOfId);

  const handleSelectPlan = (variant: any) => {
    setSelectedVariant(variant);
    setWizardOpen(true);
  };

  const handleGenerateInstalationLink = async () => {
    if (!serviceId) {
      showError("ID de servicio no proporcionado");
      return;
    }

    try {
      setInstalationLoading(true);

      // Create installation payment variant for the wizard
      const installationVariant = {
        _id: "installation",
        name: "Pago de Instalación",
        price: service?.installationInfo.total, // Will be set from API response if available
        period: 0,
        chillOfId: null,
        isInstallation: true,
      };
      setSelectedVariant(installationVariant);
      setWizardOpen(true);
    } catch (err) {
      console.error("Error preparing installation payment:", err);
      showError("Error al preparar la compra del paquete de navegación. Por favor, intenta nuevamente.");
    } finally {
      setInstalationLoading(false);
    }
  };

  useEffect(() => {
    const findService = async () => {
      if (!serviceId) {
        showError("ID de servicio no proporcionado");
        navigate("/dashboard");
        return;
      }

      try {
        setLoading(true);
        const found = services?.find(
          (s: serviceStruct) => String(s._id ?? s.documentNumber) === serviceId
        );

        if (found) {
          setService(found);
        } else {
          showError("Servicio no encontrado");
          navigate("/dashboard");
        }
      } catch (err) {
        console.error("Error finding service:", err);
        showError("Error al cargar el servicio. Por favor, intenta nuevamente.");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };

    findService();
  }, [serviceId, services, navigate, showError]);

  if (loading) {
    return (
      <MainLayout title="Cargando..." description="">
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography>Cargando detalles del servicio...</Typography>
        </Box>
      </MainLayout>
    );
  }

  if (!service) {
    return (
      <MainLayout title="Servicio no encontrado" description="">
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography>Servicio no encontrado</Typography>
        </Box>
      </MainLayout>
    );
  }

  const title = service.planInfo?.planName ?? service.documentNumber ?? "Servicio";
  const planData = service.plan[0];
  const servicePrice = planData.price ?? 0;
  const serviceAddress = service.serviceInstalledAddress ?? "-";
  const status = service.serviceState ?? service.paymentStatus ?? "Activo";
  const statusColor = status.toLowerCase().includes("activo") ? "hsl(142 71.8% 29.2%)" : "hsl(0 84.2% 60.2%)";
  const isActive = status.toLowerCase().includes("activo");
  const lastInvoice = service.planInvoice[0]
  const isPostpaid = planData?.serviceType?.toLowerCase().includes("pospago");
  const isPendingInstallation = service.installationInfo.status === "INSTALADO";

  function getServiceIcon(type?: string) {
    if (!type) return <Smartphone size={32} />;
    const t = type.toLowerCase();
    if (t.includes("internet") || t.includes("inet")) return <Wifi size={32} />;
    return <Smartphone size={32} />;
  }

  return (
    <MainLayout>
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%)",
          pb: 6,
          px: { xs: 0, sm: 3, md: 4 },
          pt: { xs: 3, sm: 4 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='netGrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%233b82f6;stop-opacity:0.08' /%3E%3Cstop offset='100%25' style='stop-color:%234f46e5;stop-opacity:0.06' /%3E%3C/linearGradient%3E%3C/defs%3E%3C!-- Nodos principales --%3E%3Ccircle cx='20' cy='20' r='4' fill='%233b82f6' opacity='0.15' /%3E%3Ccircle cx='100' cy='30' r='3' fill='%234f46e5' opacity='0.12' /%3E%3Ccircle cx='60' cy='60' r='4.5' fill='%233b82f6' opacity='0.14' /%3E%3Ccircle cx='20' cy='100' r='3' fill='%234f46e5' opacity='0.11' /%3E%3Ccircle cx='100' cy='90' r='4' fill='%233b82f6' opacity='0.13' /%3E%3Ccircle cx='40' cy='40' r='3' fill='%234f46e5' opacity='0.1' /%3E%3Ccircle cx='80' cy='70' r='3.5' fill='%233b82f6' opacity='0.12' /%3E%3C!-- Líneas de conexión --%3E%3Cline x1='20' y1='20' x2='40' y2='40' stroke='%233b82f6' stroke-width='1' opacity='0.08' /%3E%3Cline x1='40' y1='40' x2='60' y2='60' stroke='%234f46e5' stroke-width='1' opacity='0.07' /%3E%3Cline x1='60' y1='60' x2='80' y2='70' stroke='%233b82f6' stroke-width='1' opacity='0.08' /%3E%3Cline x1='100' cy='30' x2='80' y2='70' stroke='%234f46e5' stroke-width='1' opacity='0.07' /%3E%3Cline x1='20' y1='100' x2='60' y2='60' stroke='%233b82f6' stroke-width='1' opacity='0.08' /%3E%3Cline x1='60' y1='60' x2='100' y2='90' stroke='%234f46e5' stroke-width='1' opacity='0.07' /%3E%3Cline x1='20' y1='20' x2='100' y2='90' stroke='%233b82f6' stroke-width='0.8' opacity='0.05' /%3E%3C/svg%3E")
            `,
            backgroundSize: "120px 120px",
            pointerEvents: "none",
            zIndex: 0,
          },
        }}
      >
        <Box sx={{ maxWidth: 900, mx: "auto", position: "relative", zIndex: 1 }}>
          {/* Back Button */}
          <Button
            startIcon={<ArrowLeft size={20} />}
            onClick={() => navigate("/dashboard")}
            sx={{
              mb: 4,
              color: "hsl(224.3 76.3% 48%)",
              textTransform: "none",
              fontSize: "0.95rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "rgba(59, 130, 246, 0.08)",
              },
            }}
          >
            Volver a Servicios
          </Button>

          {/* Main Service Card */}
          <Card
            sx={{
              p: { xs: 4, sm: 4 },
              bgcolor: "hsl(0 0% 100%)",
              border: "1px solid hsl(240 4.8% 95.9%)",
              borderRadius: "20px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.08)",
              mb: 3,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {/* Header Section */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, mb: 4, pb: 4, borderBottom: "1px solid hsl(240 4.8% 95.9%)" }}>
              <Box sx={{
                color: "hsl(224.3 76.3% 48%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                backgroundColor: "hsl(224.3 76.3% 95%)",
                borderRadius: "14px",
                flexShrink: 0
              }}>
                {getServiceIcon(service.serviceType)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: "hsl(240 10% 3.9%)", fontSize: { xs: "1.4rem", sm: "1.95rem" }, letterSpacing: "-0.5px" }}>
                    {title}
                  </Typography>
                  <Chip
                    icon={isActive ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                    label={status}
                    sx={{
                      backgroundColor: statusColor,
                      color: "white",
                      fontWeight: 700,
                      height: "28px",
                    }}
                  />
                  <Chip
                    label={isPostpaid ? "Pospago" : "Prepago"}
                    sx={{
                      backgroundColor: "hsl(224.3 76.3% 97%)",
                      color: "hsl(224.3 76.3% 48%)",
                      fontWeight: 700,
                      border: "1px solid hsl(224.3 76.3% 85%)",
                      height: "28px",
                    }}
                  />
                </Box>
                {planData?.downloadSpeed && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "hsl(240 3.8% 46.1%)", marginTop: 1.5, mb: 1.5 }}>
                    <Zap size={16} style={{ flexShrink: 0, color: "hsl(224.3 76.3% 48%)" }} />
                    <Typography variant="body2" sx={{ fontSize: "0.9rem", lineHeight: 1.4 }}>
                      <span style={{ fontWeight: 600, color: "hsl(240 10% 3.9%)" }}>Velocidad de Internet:</span> {planData.downloadSpeed} Mbps
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "hsl(240 3.8% 46.1%)", marginTop: 1 }}>
                  <MapPin size={16} style={{ flexShrink: 0, opacity: 0.7 }} />
                  <Typography variant="body2" sx={{ fontSize: "0.9rem", lineHeight: 1.4 }}>{serviceAddress}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Installation Payment Alert */}
            {isPendingInstallation && (
              <Box sx={{
                p: { xs: 2.5, sm: 3.5 },
                backgroundColor: "hsl(34.3 100% 97%)",
                borderRadius: "16px",
                mb: 4,
                border: "2px solid hsl(34.3 97.3% 70%)",
                transition: "all 0.3s ease"
              }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2.5 }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "hsl(34.3 97.3% 40%)",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "block",
                        mb: 1.5,
                        fontSize: "0.7rem"
                      }}
                    >
                      💳 Compra tu paquete de navegación ahora
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "hsl(240 10% 3.9%)", mb: 1.5, fontSize: "1.1rem" }}>
                      Compra tu primer paquete de navegación por 30 días y completa tu proceso de instalación
                    </Typography>
                    <Typography variant="body2" sx={{ color: "hsl(240 3.8% 46.1%)", mb: 2, lineHeight: 1.6 }}>
                      Por favor, compra tu paquete de navegación para activar completamente tu servicio.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleGenerateInstalationLink}
                      disabled={instalationLoading}
                      sx={{
                        backgroundColor: "hsl(34.3 97.3% 50.4%)",
                        color: "white",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        py: 1.2,
                        px: 3,
                        "&:hover": {
                          backgroundColor: "hsl(34.3 97.3% 45%)",
                        },
                        "&:disabled": {
                          backgroundColor: "hsl(240 3.8% 74%)",
                          color: "white",
                        }
                      }}
                    >
                      {instalationLoading ? "Generando enlace..." : "Comprar paquete de navegación"}
                    </Button>
                  </Box>
                  <AlertTriangle size={40} color="hsl(34.3 97.3% 45%)" style={{ flexShrink: 0 }} />
                </Box>
              </Box>
            )}

            {/* Service Expiration Alert - Above Tabs */}
            {lastInvoice?.expiresAt && (service.installationInfo.status !== "INSTALADO" && service.installationInfo.status !== "INSTALACION EN CURSO" && service.installationInfo.status !== "PENDIENTE DE INSTALACION") && (
              <Box sx={{
                p: { xs: 2.5, sm: 3.5 },
                backgroundColor: calculateRemainingDays(lastInvoice.expiresAt) <= 1
                  ? "hsl(0 84.2% 97%)"
                  : calculateRemainingDays(lastInvoice.expiresAt) <= 7
                    ? "hsl(34.3 100% 97%)"
                    : "hsl(224.3 76.3% 97%)",
                borderRadius: "16px",
                mb: 4,
                border: `2px solid ${calculateRemainingDays(lastInvoice.expiresAt) <= 1
                  ? "hsl(0 84.2% 70%)"
                  : calculateRemainingDays(lastInvoice.expiresAt) <= 7
                    ? "hsl(34.3 97.3% 70%)"
                    : "hsl(224.3 76.3% 75%)"
                  }`,
                transition: "all 0.3s ease"
              }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2.5 }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: calculateRemainingDays(lastInvoice.expiresAt) <= 1
                          ? "hsl(0 84.2% 40%)"
                          : calculateRemainingDays(lastInvoice.expiresAt) <= 7
                            ? "hsl(34.3 97.3% 40%)"
                            : "hsl(224.3 76.3% 40%)",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "block",
                        mb: 1.5,
                        fontSize: "0.7rem"
                      }}
                    >
                      ⏱️ Servicio válido hasta
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "hsl(240 10% 3.9%)", mb: 1.5, fontSize: "1.1rem" }}>
                      {new Date(lastInvoice.expiresAt).toLocaleDateString("es-ES", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: calculateRemainingDays(lastInvoice.expiresAt) < 0
                          ? "hsl(0 84.2% 50%)"
                          : calculateRemainingDays(lastInvoice.expiresAt) <= 1
                            ? "hsl(0 84.2% 50%)"
                            : calculateRemainingDays(lastInvoice.expiresAt) <= 7
                              ? "hsl(34.3 97.3% 40%)"
                              : "hsl(224.3 76.3% 45%)",
                        fontWeight: 800,
                        fontSize: "1.25rem"
                      }}
                    >
                      {calculateRemainingDays(lastInvoice.expiresAt) < 0
                        ? "Vencido"
                        : calculateRemainingDays(lastInvoice.expiresAt) === 0 || calculateRemainingDays(lastInvoice.expiresAt) === 1
                          ? `Hoy a las ${new Date(lastInvoice.expiresAt).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}`
                          : `${calculateRemainingDays(lastInvoice.expiresAt)} días restantes`
                      }
                    </Typography>
                  </Box>
                  {calculateRemainingDays(lastInvoice.expiresAt) <= 1 && calculateRemainingDays(lastInvoice.expiresAt) >= 0 && (
                    <AlertTriangle size={40} color="hsl(0 84.2% 50%)" style={{ flexShrink: 0 }} />
                  )}
                  {calculateRemainingDays(lastInvoice.expiresAt) > 1 && calculateRemainingDays(lastInvoice.expiresAt) <= 7 && (
                    <AlertTriangle size={40} color="hsl(34.3 97.3% 45%)" style={{ flexShrink: 0 }} />
                  )}
                  {calculateRemainingDays(lastInvoice.expiresAt) > 7 && (
                    <AlertTriangle size={40} color="hsl(224.3 76.3% 50%)" style={{ flexShrink: 0, opacity: 0.5 }} />
                  )}
                </Box>
              </Box>
            )}

            {/* Tabs Section */}
            <Box sx={{ mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  borderBottom: "2px solid hsl(240 4.8% 95.9%)",
                  mb: 3,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    fontWeight: 500,
                    color: "hsl(224.3 76.3% 48%)",
                    minHeight: { xs: "48px", sm: "auto" },
                    padding: { xs: "8px 12px", sm: "12px 16px" },
                    transition: "all 0.2s ease",
                    borderRadius: "8px 8px 0 0",
                    "&:hover": {
                      backgroundColor: "hsl(224.3 76.3% 96%)",
                    },
                    "&.Mui-selected": {
                      color: "hsl(224.3 76.3% 100%)",
                      fontWeight: 700,
                      backgroundColor: "hsl(224.3 76.3% 48%)",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}
              >

                {/* {isPostpaid && (
                  <Tab 
                    icon={<Zap size={18} />} 
                    iconPosition="start" 
                    label="Planes" 
                    value="plan" 
                  />
                )} */}
                {!isPostpaid && (
                  <Tab
                    icon={<FileText size={18} />}
                    iconPosition="start"
                    label="Recargas"
                    value="recharge"
                  />
                )}
                {/* <Tab
                  icon={<Wifi size={18} />}
                  iconPosition="start"
                  label={window.innerWidth < 600 ? "Mi Red" : "Mi Red"}
                  value="diagnostics"
                /> */}
                <Tab
                  icon={<Info size={18} />}
                  iconPosition="start"
                  label={window.innerWidth < 600 ? "Info" : "Información"}
                  value="info"
                />
                <Tab
                  icon={<Wifi size={18} />}
                  iconPosition="start"
                  label="Mi WiFi"
                  value="wifi"
                />
              </Tabs>
              {/* Wifi Settings tab */}


              {/* Tab Content */}
              <Box>
                {/* Tab: Información del Servicio */}
                {activeTab === "info" && (
                  <Box>
                    {/* Location Section */}
                    {service.city && (
                      <Box sx={{ p: 3, backgroundColor: "hsl(240 4.8% 95.9%)", borderRadius: "14px", mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                          <MapPin size={24} color="hsl(224.3 76.3% 48%)" />
                          <Typography variant="h6" sx={{ fontWeight: 700, color: "hsl(240 10% 3.9%)" }}>
                            Ubicación del Servicio
                          </Typography>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 2 }}>
                          {service.department && (
                            <Box sx={{ p: 2, backgroundColor: "white", borderRadius: "12px", border: "1px solid hsl(240 4.8% 95.9%)" }}>
                              <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)", fontWeight: 700, display: "block", mb: 1, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Departamento
                              </Typography>
                              <Typography variant="body2" sx={{ color: "hsl(240 10% 3.9%)", fontWeight: 600 }}>
                                {service.department}
                              </Typography>
                            </Box>
                          )}
                          <Box sx={{ p: 2, backgroundColor: "white", borderRadius: "12px", border: "1px solid hsl(240 4.8% 95.9%)" }}>
                            <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)", fontWeight: 700, display: "block", mb: 1, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              Ciudad
                            </Typography>
                            <Typography variant="body2" sx={{ color: "hsl(240 10% 3.9%)", fontWeight: 600 }}>
                              {service.city}
                            </Typography>
                          </Box>
                          {service.neighborhood && (
                            <Box sx={{ p: 2, backgroundColor: "white", borderRadius: "12px", border: "1px solid hsl(240 4.8% 95.9%)" }}>
                              <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)", fontWeight: 700, display: "block", mb: 1, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Barrio
                              </Typography>
                              <Typography variant="body2" sx={{ color: "hsl(240 10% 3.9%)", fontWeight: 600 }}>
                                {service.neighborhood}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Service Information Grid */}
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 3 }}>
                      {/* POSPAGO: Plan Information */}
                      {isPostpaid && (
                        <Box sx={{ p: 2, backgroundColor: "hsl(240 4.8% 95.9%)", borderRadius: "14px", display: "flex", flexDirection: "column", gap: 2 }}>
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "hsl(240 3.8% 46.1%)",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                                display: "block",
                                mb: 1,
                              }}
                            >
                              Tarifa mensual
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: "hsl(224.3 76.3% 48%)" }}>
                              {formatCurrency(servicePrice)}
                            </Typography>
                          </Box>
                          {service.paymentLink && (
                            <Button
                              variant="contained"
                              onClick={() => {
                                const paymentVariant = {
                                  _id: service.plan[0]?._id || service.planInfo?.planId || "postpago",
                                  name: service.planInfo?.planName || "Pago de factura",
                                  price: servicePrice,
                                  period: 30,
                                  chillOfId: null,
                                };
                                setSelectedVariant(paymentVariant);
                                setWizardOpen(true);
                              }}
                              sx={{
                                backgroundColor: "hsl(224.3 76.3% 48%)",
                                color: "white",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                py: 1,
                                "&:hover": {
                                  backgroundColor: "hsl(217.2 91.2% 59.8%)",
                                },
                              }}
                            >
                              Realizar un pago en linea
                            </Button>
                          )}
                        </Box>
                      )}

                      {/* Contract Date */}
                      <Box sx={{ p: 2, backgroundColor: "hsl(240 4.8% 95.9%)", borderRadius: "14px" }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "hsl(240 3.8% 46.1%)",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.3px",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Fecha de contratación
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "hsl(240 10% 3.9%)" }}>
                          {service.dateContracted ? new Date(service.dateContracted).toLocaleDateString("es-ES") : "No especificada"}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Continue Service Message for Prepago - Within Info Tab */}
                    {/* {!isPostpaid && (
                      <Box sx={{ p: 3, backgroundColor: "hsl(240 4.8% 95.9%)", borderRadius: "8px", border: "2px solid hsl(34.3 97.3% 50.4%)", mt: 3 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "hsl(240 10% 3.9%)",
                            fontWeight: 600,
                            mb: 2,
                            lineHeight: 1.6,
                          }}
                        >
                          ¿Deseas continuar con el mismo servicio?
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "hsl(240 3.8% 46.1%)",
                            display: "block",
                            mb: 2,
                          }}
                        >
                          Realiza el pago para renovar tu servicio sin interrupción.
                        </Typography>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => window.open(lastInvoice?.paymentURL || service.paymentLink, "_blank")}
                          sx={{
                            backgroundColor: "hsl(34.3 97.3% 50.4%)",
                            color: "white",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            py: 1.5,
                            "&:hover": {
                              backgroundColor: "hsl(34.3 97.3% 45%)",
                            },
                          }}
                        >
                          Renovar Servicio
                        </Button>
                      </Box>
                    )} */}
                  </Box>
                )}

                {/* Tab: WiFi Settings */}
                {activeTab === "wifi" && serviceId && (
                  <WifiSettings serviceId={serviceId} />
                )}

                {/* Tab: Planes (Solo Pospago) */}
                {activeTab === "plan" && isPostpaid && (
                  <Box sx={{ p: 3, backgroundColor: "hsl(240 4.8% 95.9%)", borderRadius: "14px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                      <Zap size={24} color="hsl(224.3 76.3% 48%)" />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "hsl(240 10% 3.9%)" }}>
                        {service.planInfo?.planName || "Plan de servicio"}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)", fontWeight: 700, display: "block", mb: 1, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          ID del Plan
                        </Typography>
                        <Typography variant="body2" sx={{ color: "hsl(240 10% 3.9%)", fontFamily: "monospace" }}>
                          {service.planInfo?.planId || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)", fontWeight: 700, display: "block", mb: 1, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Precio mensual
                        </Typography>
                        <Typography variant="body2" sx={{ color: "hsl(224.3 76.3% 48%)", fontWeight: 700, fontSize: "1.1rem" }}>
                          {formatCurrency(service.planInfo?.total)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)", fontWeight: 700, display: "block", mb: 1, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Subtotal
                        </Typography>
                        <Typography variant="body2" sx={{ color: "hsl(240 10% 3.9%)" }}>
                          {formatCurrency(service.planInfo?.subTotal)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)", fontWeight: 700, display: "block", mb: 1, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Impuesto
                        </Typography>
                        <Typography variant="body2" sx={{ color: "hsl(240 10% 3.9%)" }}>
                          {formatCurrency(service.planInfo?.taxmount)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Tab: Recarga (Solo Prepago) */}
                {activeTab === "recharge" && !isPostpaid && (
                  <Box>
                    {/* <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 3, mb: 3 }}>
                      <Box sx={{ p: 2, backgroundColor: "hsl(240 4.8% 95.9%)", borderRadius: "8px" }}>
                        <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)", fontWeight: 600, display: "block", mb: 1 }}>
                          Crédito Disponible
                        </Typography>
                        <Typography variant="h6" sx={{ color: "hsl(34.3 97.3% 50.4%)", fontWeight: 700 }}>
                          Disponible
                        </Typography>
                      </Box>
                      <Box sx={{ p: 2, backgroundColor: "hsl(240 4.8% 95.9%)", borderRadius: "8px" }}>
                        <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)", fontWeight: 600, display: "block", mb: 1 }}>
                          Válido hasta
                        </Typography>
                        <Typography variant="body2" sx={{ color: "hsl(240 10% 3.9%)" }}>
                          {service.dateContracted ? new Date(service.dateContracted).toLocaleDateString("es-ES") : "No especificada"}
                        </Typography>
                      </Box>
                    </Box> */}

                    {/* Planes disponibles */}
                    <Box sx={{ mb: 3 }}>
                      {plansLoading ? (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
                          <Loader size={24} color="hsl(224.3 76.3% 48%)" />
                          <Typography variant="body2" sx={{ ml: 2, color: "hsl(240 3.8% 46.1%)" }}>
                            Cargando planes...
                          </Typography>
                        </Box>
                      ) : rootPlans.length > 0 ? (
                        <Box>
                          {/* Plan Raíz - Encabezado */}
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: "hsl(240 10% 3.9%)",
                                }}
                              >
                                {rootPlans[0].name}
                              </Typography>
                              {planData?.downloadSpeed && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "hsl(240 3.8% 46.1%)" }}>
                                  <Zap size={16} style={{ flexShrink: 0, color: "hsl(224.3 76.3% 48%)" }} />
                                  <Typography variant="body2" sx={{ fontSize: "0.9rem", lineHeight: 1.4 }}>
                                    <span style={{ fontWeight: 600, color: "hsl(240 10% 3.9%)" }}>Velocidad de Internet:</span> {planData.downloadSpeed} Mbps
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            {rootPlans[0].description && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "hsl(240 3.8% 46.1%)",
                                  mb: 2,
                                }}
                              >
                                {rootPlans[0].description}
                              </Typography>
                            )}
                          </Box>

                          {/* Variantes */}
                          {(() => {
                            const variants = plans.filter((p: any) => p.chillOfId === rootPlans[0]._id);
                            return variants.length > 0 ? (
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    color: "hsl(240 10% 3.9%)",
                                    mb: 2,
                                  }}
                                >
                                  Selecciona una opción
                                </Typography>
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 3 }}>
                                  {!instalationProcess && variants.filter((variant: any) => variant.delete !== true && variant.disable !== true).map((variant: any, vIdx: number) => (
                                    <Card
                                      key={vIdx}
                                      sx={{
                                        p: 3,
                                        backgroundColor: "white",
                                        border: "1px solid hsl(240 4.8% 95.9%)",
                                        borderRadius: "14px",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 2,
                                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                        cursor: "pointer",
                                        "&:hover": {
                                          borderColor: "hsl(224.3 76.3% 48%)",
                                          boxShadow: "0 8px 20px rgba(59, 130, 246, 0.15)",
                                          transform: "translateY(-2px)",
                                        },
                                      }}
                                    >
                                      <Box>
                                        <Typography
                                          variant="subtitle1"
                                          sx={{
                                            fontWeight: 700,
                                            color: "hsl(240 10% 3.9%)",
                                            mb: 1,
                                          }}
                                        >
                                          {variant.name}
                                        </Typography>
                                        {variant.description && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "hsl(240 3.8% 46.1%)",
                                              display: "block",
                                            }}
                                          >
                                            {variant.description}
                                          </Typography>
                                        )}
                                      </Box>

                                      <Box sx={{ display: "grid", gap: 2, mb: 2 }}>
                                        {variant.price !== undefined && (
                                          <Box>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "hsl(240 3.8% 46.1%)",
                                                fontWeight: 600,
                                                display: "block",
                                                mb: 0.5,
                                              }}
                                            >
                                              Precio
                                            </Typography>
                                            <Typography
                                              variant="h6"
                                              sx={{
                                                color: "hsl(34.3 97.3% 50.4%)",
                                                fontWeight: 700,
                                              }}
                                            >
                                              {formatCurrency(variant.price)}
                                            </Typography>
                                          </Box>
                                        )}
                                        {variant.period && (
                                          <Box>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "hsl(240 3.8% 46.1%)",
                                                fontWeight: 600,
                                                display: "block",
                                                mb: 0.5,
                                              }}
                                            >
                                              Duración
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "hsl(240 10% 3.9%)" }}>
                                              {variant.period} días
                                            </Typography>
                                          </Box>
                                        )}
                                        {(variant.downloadSpeed || variant.uploadSpeed) && (
                                          <Box>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "hsl(240 3.8% 46.1%)",
                                                fontWeight: 600,
                                                display: "block",
                                                mb: 0.5,
                                              }}
                                            >
                                              Velocidad
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "hsl(240 10% 3.9%)" }}>
                                              {variant.downloadSpeed && `${variant.downloadSpeed} Mbps`}
                                              {variant.downloadSpeed && variant.uploadSpeed && " / "}
                                              {variant.uploadSpeed && `↑ ${variant.uploadSpeed} Mbps`}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>

                                      <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => handleSelectPlan(variant)}
                                        startIcon={<ShoppingCart size={18} />}
                                        sx={{
                                          backgroundColor: "hsl(34.3 97.3% 50.4%)",
                                          color: "white",
                                          textTransform: "none",
                                          fontWeight: 600,
                                          fontSize: "0.875rem",
                                          py: 1,
                                          mt: "auto",
                                          "&:hover": {
                                            backgroundColor: "hsl(34.3 97.3% 45%)",
                                          },
                                        }}
                                      >
                                        Comprar
                                      </Button>
                                    </Card>
                                  ))}
                                  {isPendingInstallation && (
                                    <Box sx={{ p: 3, backgroundColor: "hsl(240 4.8% 95.9%)", borderRadius: "8px", textAlign: "center", gridColumn: "1 / -1" }}>
                                      <Typography variant="body2" sx={{ color: "hsl(240 3.8% 46.1%)" }}>
                                        Compra tu paquete de navegación para ver las opciones de recarga disponibles.
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ p: 3, backgroundColor: "hsl(240 4.8% 95.9%)", borderRadius: "8px", textAlign: "center" }}>
                                <Typography variant="body2" sx={{ color: "hsl(240 3.8% 46.1%)" }}>
                                  No hay variantes disponibles para este plan.
                                </Typography>
                              </Box>
                            );
                          })()}
                        </Box>
                      ) : (
                        <Box sx={{ p: 3, backgroundColor: "hsl(240 4.8% 95.9%)", borderRadius: "8px", textAlign: "center" }}>
                          <Typography variant="body2" sx={{ color: "hsl(240 3.8% 46.1%)" }}>
                            No hay planes disponibles en este momento.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Tab: Diagnósticos */}
                {activeTab === "diagnostics" && (
                  <Box sx={{ p: 3, backgroundColor: "hsl(240 4.8% 95.9%)", borderRadius: "14px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                      <Activity size={24} color="hsl(142 71.8% 29.2%)" />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "hsl(240 10% 3.9%)" }}>
                        Diagnósticos y Estado
                      </Typography>
                    </Box>
                    {service.networkInfo && (service.networkInfo.oltId || service.networkInfo.onu) && (
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2, mb: 3 }}>
                        {service.networkInfo.oltId && (
                          <Box sx={{ p: 2, backgroundColor: "white", borderRadius: "12px", border: "1px solid hsl(240 4.8% 95.9%)" }}>
                            <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)", fontWeight: 700, display: "block", mb: 1, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              OLT ID
                            </Typography>
                            <Typography variant="body2" sx={{ color: "hsl(240 10% 3.9%)", wordBreak: "break-all", fontFamily: "monospace" }}>
                              {service.networkInfo.oltId}
                            </Typography>
                          </Box>
                        )}
                        {service.networkInfo.onu && (
                          <Box sx={{ p: 2, backgroundColor: "white", borderRadius: "12px", border: "1px solid hsl(240 4.8% 95.9%)" }}>
                            <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)", fontWeight: 700, display: "block", mb: 1, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              ONU
                            </Typography>
                            <Typography variant="body2" sx={{ color: "hsl(240 10% 3.9%)", wordBreak: "break-all", fontFamily: "monospace" }}>
                              {service.networkInfo.onu}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                    <Box sx={{ p: 2, backgroundColor: "white", borderRadius: "12px", border: "1px solid hsl(240 4.8% 95.9%)" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <CheckCircle2 size={20} color="hsl(142 71.8% 29.2%)" />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "hsl(240 10% 3.9%)" }}>
                          Estado del Servicio
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: "hsl(240 3.8% 46.1%)" }}>
                        {status}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Service ID - Bottom Right Corner */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2, mt: 2, borderTop: "1px solid hsl(240 5.9% 90%)" }}>
              <Typography
                variant="caption"
                sx={{
                  color: "hsl(240 3.8% 46.1%)",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                ID: {service._id}
              </Typography>
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Recharge Wizard Dialog */}
      {selectedVariant && (
        <RechargeWizard
          open={wizardOpen}
          onClose={() => {
            setWizardOpen(false);
            setSelectedVariant(null);
          }}
          serviceId={serviceId || ""}
          planVariant={selectedVariant}
          service={service}
        />
      )}
    </MainLayout>
  );
}
